export interface TrackTitleDisplay {
  original: string;
  display: string;
}

export interface TrackTitlePrefixResult {
  prefix: string | null;
  items: TrackTitleDisplay[];
}

/**
 * Removes a shared prefix from a list of track titles when the prefix is long enough to matter.
 *
 * Designed to be invoked at render time so we avoid mutating persisted data.
 */
const normalizeForPrefix = (value: string) => {
  const indexMap: number[] = [];
  let canonical = '';

  const translateQuote = (char: string): string => {
    switch (char) {
      case '\u2018':
      case '\u2019':
      case '\u201A':
      case '\u201B':
      case '\u2032':
        return "'";
      case '\u201C':
      case '\u201D':
      case '\u201E':
      case '\u201F':
      case '\u2033':
        return '"';
      default:
        return char;
    }
  };
  const isQuote = (char: string): boolean =>
    char === '"' || char === "'" || /[\u2018\u2019\u201A\u201B\u201C\u201D\u201E\u201F\u2032\u2033]/.test(char);

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index] ?? '';
    const translated = translateQuote(char === '\u00a0' ? ' ' : char);
    const nextChar = value[index + 1] ?? '';
    const lastCanonicalChar = canonical.length > 0 ? canonical[canonical.length - 1] : '';
    if (translated === ' ' && nextChar && isQuote(translateQuote(nextChar))) {
      continue;
    }
    if (translated === ' ' && lastCanonicalChar && isQuote(lastCanonicalChar)) {
      continue;
    }
    canonical += translateQuote(translated);
    indexMap.push(index);
  }

  return { canonical, indexMap };
};

export const deriveTrackTitlePrefix = (
  titles: Array<string | null | undefined>,
  _recordingLabel?: string
): TrackTitlePrefixResult => {
  const processed = titles.map((title) => {
    const original = typeof title === 'string' ? title : '';
    return {
      original,
      ...normalizeForPrefix(original)
    };
  });

  const meaningfulEntries = processed
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.original.length > 0 && entry.canonical.length > 0);

  if (meaningfulEntries.length === 0) {
    return {
      prefix: null,
      items: processed.map(({ original }) => ({ original, display: original }))
    };
  }

  if (meaningfulEntries.length === 1) {
    return {
      prefix: null,
      items: processed.map(({ original }) => ({ original, display: original }))
    };
  }

  let prefixLength = meaningfulEntries[0]?.entry.canonical.length ?? 0;
  const referenceCanonical = meaningfulEntries[0]?.entry.canonical ?? '';

  for (let i = 1; i < meaningfulEntries.length && prefixLength > 0; i += 1) {
    const candidate = meaningfulEntries[i]?.entry.canonical ?? '';
    const maxComparableLength = Math.min(prefixLength, candidate.length);
    let shared = 0;
    while (shared < maxComparableLength && referenceCanonical[shared] === candidate[shared]) {
      shared += 1;
    }
    prefixLength = shared;
  }

  if (prefixLength < 6) {
    return {
      prefix: null,
      items: processed.map(({ original }) => ({ original, display: original }))
    };
  }

  const delimiters = [':', '-', '\u2013', '\u2014'];
  const canonicalPrefixOriginal = referenceCanonical.slice(0, prefixLength);
  const delimiterIndex = delimiters.reduce((maxIndex, delimiter) => {
    const index = canonicalPrefixOriginal.lastIndexOf(delimiter);
    return index > maxIndex ? index : maxIndex;
  }, -1);
  if (delimiterIndex >= 0 && delimiterIndex + 1 < prefixLength) {
    prefixLength = delimiterIndex + 1;
  }

  if (prefixLength < 6) {
    return {
      prefix: null,
      items: processed.map(({ original }) => ({ original, display: original }))
    };
  }

  const firstEntry = meaningfulEntries[0]?.entry ?? null;
  const prefixEndIndex =
    prefixLength > 0 && firstEntry && firstEntry.indexMap.length >= prefixLength
      ? firstEntry.indexMap[prefixLength - 1] + 1
      : 0;
  const prefix =
    prefixEndIndex && firstEntry
      ? firstEntry.original.slice(0, prefixEndIndex)
      : null;

  const items = processed.map((entry) => {
    if (!entry.original.length) {
      return { original: entry.original, display: entry.original };
    }
    const entryIndex =
      prefixLength > 0 && entry.indexMap.length >= prefixLength
        ? entry.indexMap[prefixLength - 1] + 1
        : 0;
    if (!entryIndex) {
      return { original: entry.original, display: entry.original };
    }
    const candidate = entry.original.slice(entryIndex).replace(/^\s+/, '');
    return {
      original: entry.original,
      display: candidate.length > 0 ? candidate : entry.original
    };
  });

  const changed = items.some((item) => item.original !== item.display);
  if (!changed || !prefix) {
    return {
      prefix: null,
      items: processed.map(({ original }) => ({ original, display: original }))
    };
  }

  return {
    prefix,
    items
  };
};
